import React, { useState, useEffect } from "react";
import {
  Table, Card, Row, Col, Select, Button, Upload, Modal, Form, Input, message, Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

const { Option } = Select;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

export const ExcludedLoanList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState("");
  const [filters, setFilters] = useState({ month: undefined, year: undefined });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const token = JSON.parse(sessionStorage.getItem("user"))?.accessToken;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);
      const res = await axios.get(`${Api_Url}api/v1/ExcludedLoan?${params.toString()}`, authHeader);
      setRows(res?.data?.data || []);
    } catch (e) {
      ErrorToast("Failed to fetch excluded loans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const submitAdd = async (values) => {
    setSaving(true);
    try {
      const payload = {
        LoanNumber: values.LoanNumber?.trim(),
        ExcludedMonth: values.ExcludedMonth,
        ExcludedYear: values.ExcludedYear,
      };
      const res = await axios.post(`${Api_Url}api/v1/ExcludedLoan`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        SuccessToast("Excluded loan added");
        setIsAddOpen(false);
        addForm.resetFields();
        fetchRows();
      }
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Failed to add excluded loan");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      message.warning("Please choose an Excel file first.");
      return;
    }
    setUploading(true);
    setReport("");
    try {
      const formData = new FormData();
      formData.append("ExcelFile", file);
      const res = await axios.post(
        `${Api_Url}api/v1/ExcludedLoan/upload-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      SuccessToast(res?.data?.message || "Upload complete");
      setReport(res?.data?.data || "");
      setFile(null);
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Upload failed");
      if (e.response?.data?.data) setReport(e.response.data.data);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ["Loan Number", "Excluded Month", "Excluded Year"];
    const sample = ["LN00012345", "9", "2026"];
    const csv = headers.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ExcludedLoan_Template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70, sorter: (a, b) => a.id - b.id, defaultSortOrder: "descend" },
    { title: "Loan Number", dataIndex: "loanNumber", sorter: (a, b) => (a.loanNumber || "").localeCompare(b.loanNumber || "") },
    { title: "Excluded Month", dataIndex: "excludedMonth", render: (m) => MONTHS[m - 1] || m, sorter: (a, b) => (a.excludedMonth || 0) - (b.excludedMonth || 0) },
    { title: "Excluded Year", dataIndex: "excludedYear", sorter: (a, b) => (a.excludedYear || 0) - (b.excludedYear || 0) },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Excluded Loan List</h3>
            <button className="btn btn-primary" onClick={() => { addForm.resetFields(); setIsAddOpen(true); }}>
              <i className="fa-solid fa-plus me-2"></i> Add Excluded Loan
            </button>
          </div>
        </div>

        <Card className="mb-4">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={8} md={5}>
              <label className="mb-1 d-block">Month</label>
              <Select
                placeholder="All months"
                value={filters.month}
                onChange={(v) => setFilters((p) => ({ ...p, month: v }))}
                allowClear
                style={{ width: "100%" }}
              >
                {MONTHS.map((m, i) => (<Option key={i + 1} value={i + 1}>{m}</Option>))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={5}>
              <label className="mb-1 d-block">Year</label>
              <Select
                placeholder="All years"
                value={filters.year}
                onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
                allowClear
                style={{ width: "100%" }}
              >
                {YEARS.map((y) => (<Option key={y} value={y}>{y}</Option>))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={14}>
              <label className="mb-1 d-block">Bulk Upload</label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Upload
                  beforeUpload={(f) => { setFile(f); return false; }}
                  maxCount={1}
                  fileList={file ? [file] : []}
                  onRemove={() => setFile(null)}
                  accept=".xlsx,.xls"
                >
                  <Button icon={<UploadOutlined />}>Choose Excel</Button>
                </Upload>
                <Button type="primary" loading={uploading} onClick={handleUpload}>Upload</Button>
                <Button onClick={downloadTemplate}>Download Template</Button>
              </div>
            </Col>
          </Row>
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message="Excel columns: Loan Number | Excluded Month (1-12 or month name) | Excluded Year. Duplicate loan + month + year rows are skipped."
          />
        </Card>

        {report && (
          <Card className="mb-4" title="Upload Report" size="small">
            <pre style={{ whiteSpace: "pre-wrap", margin: 0, maxHeight: 260, overflow: "auto" }}>{report}</pre>
          </Card>
        )}

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 10, pageSizeOptions: ["10", "20", "50", "100", "200"] }}
            scroll={{ x: 600 }}
          />
        </div>

        {/* Add modal */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Add Excluded Loan</span>}
          open={isAddOpen}
          onCancel={() => setIsAddOpen(false)}
          onOk={() => addForm.submit()}
          okText="Save"
          confirmLoading={saving}
          centered
          width={560}
        >
          <Form form={addForm} layout="vertical" onFinish={submitAdd}
            initialValues={{ ExcludedMonth: new Date().getMonth() + 1, ExcludedYear: currentYear }}>
            <Form.Item name="LoanNumber" label="Loan Number" rules={[{ required: true, message: "Enter loan number" }]}>
              <Input placeholder="e.g. LN00012345" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="ExcludedMonth" label="Excluded Month" rules={[{ required: true }]}>
                  <Select>
                    {MONTHS.map((m, i) => (<Option key={i + 1} value={i + 1}>{m}</Option>))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ExcludedYear" label="Excluded Year" rules={[{ required: true }]}>
                  <Select>
                    {YEARS.map((y) => (<Option key={y} value={y}>{y}</Option>))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};
